import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';

export default function EmailTemplates() {
     const [templates, setTemplates] = useState([]);
     const [selected, setSelected] = useState(null);
     const [subject, setSubject] = useState('');
     const [htmlBody, setHtmlBody] = useState('');
     const [saving, setSaving] = useState(false);
     const [saved, setSaved] = useState(false);
     const [showPreview, setShowPreview] = useState(false);
     const previewRef = useRef(null);

     useEffect(() => {
          loadTemplates();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

     async function loadTemplates() {
          const { data } = await supabase
               .from('email_templates')
               .select('*')
               .order('created_at');
          if (data) {
               setTemplates(data);
               if (data.length > 0 && !selected) {
                    selectTemplate(data[0]);
               }
          }
     }

     function selectTemplate(t) {
          setSelected(t);
          setSubject(t.subject);
          setHtmlBody(t.html_body);
          setSaved(false);
     }

     async function handleSave() {
          if (!selected) return;
          setSaving(true);
          await supabase
               .from('email_templates')
               .update({
                    subject,
                    html_body: htmlBody,
                    updated_at: new Date().toISOString(),
               })
               .eq('id', selected.id);
          setSaving(false);
          setSaved(true);
          setTemplates(prev => prev.map(t => t.id === selected.id ? { ...t, subject, html_body: htmlBody } : t));
          setTimeout(() => setSaved(false), 3000);
     }

     // Build preview HTML with sample data
     function getPreviewHtml() {
          let html = htmlBody;
          const sampleData = {
               customer_name: 'John Smith',
               customer_email: 'john@example.com',
               customer_phone: '(602) 555-1234',
               start_date: '2026-04-01',
               end_date: '2026-04-03',
               total: '$1,950.00',
               notes: 'Please deliver before 9am',
               booking_id: '00000000-0000-0000-0000-000000000000',
               deposit_amount: '$500.00',
               damage_amount: '$150.00',
               refund_amount: '$350.00',
               has_refund: 'true',
          };

          // Replace {{variable}} placeholders
          Object.entries(sampleData).forEach(([key, value]) => {
               html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
          });

          // Handle {{#conditional}}...{{/conditional}} blocks — show them in preview
          html = html.replace(/\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, '$2');

          return html;
     }

     // Write preview into iframe
     useEffect(() => {
          if (showPreview && previewRef.current) {
               const doc = previewRef.current.contentDocument;
               doc.open();
               doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:16px;background:#333;">${getPreviewHtml()}</body></html>`);
               doc.close();
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [showPreview, htmlBody]);

     const inputClasses = 'w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-darker text-sm focus:outline-none focus:border-yellow duration-300';

     return (
          <div>
               <h1 className='text-darker text-2xl font-bold mb-6'>Email Templates</h1>

               <div className='flex flex-col laptop:flex-row gap-6'>
                    {/* Template List */}
                    <div className='laptop:w-64 shrink-0'>
                         <div className='bg-white rounded-xl border border-gray-200 overflow-hidden'>
                              {templates.map((t) => (
                                   <button
                                        key={t.id}
                                        onClick={() => selectTemplate(t)}
                                        className={`w-full text-left px-4 py-3 border-b border-gray-200 last:border-b-0 transition-colors duration-200 ${
                                             selected?.id === t.id
                                                  ? 'bg-yellow/10 text-yellow'
                                                  : 'text-darker/60 hover:text-darker hover:bg-gray-100'
                                        }`}
                                   >
                                        <p className='text-sm font-medium'>{t.name}</p>
                                        <p className='text-xs opacity-50 mt-0.5'>{t.description}</p>
                                   </button>
                              ))}
                              {templates.length === 0 && (
                                   <p className='text-darker/30 text-sm p-4'>No templates found. Run the migration to seed defaults.</p>
                              )}
                         </div>
                    </div>

                    {/* Editor */}
                    {selected && (
                         <div className='flex-1 space-y-4'>
                              {/* Subject */}
                              <div>
                                   <label className='block text-darker/40 text-xs mb-1'>Subject Line</label>
                                   <input
                                        type='text'
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className={inputClasses}
                                   />
                              </div>

                              {/* Toggle */}
                              <div className='flex items-center gap-2'>
                                   <button
                                        onClick={() => setShowPreview(false)}
                                        className={`px-3 py-1.5 rounded-lg text-sm border duration-300 ${
                                             !showPreview ? 'border-yellow text-yellow bg-yellow/10' : 'border-gray-200 text-darker/40 hover:text-darker'
                                        }`}
                                   >
                                        Edit HTML
                                   </button>
                                   <button
                                        onClick={() => setShowPreview(true)}
                                        className={`px-3 py-1.5 rounded-lg text-sm border duration-300 ${
                                             showPreview ? 'border-yellow text-yellow bg-yellow/10' : 'border-gray-200 text-darker/40 hover:text-darker'
                                        }`}
                                   >
                                        Preview
                                   </button>
                              </div>

                              {/* HTML Editor */}
                              {!showPreview && (
                                   <div>
                                        <textarea
                                             value={htmlBody}
                                             onChange={(e) => setHtmlBody(e.target.value)}
                                             className={`${inputClasses} font-mono text-xs leading-relaxed`}
                                             rows={22}
                                             spellCheck={false}
                                        />
                                   </div>
                              )}

                              {/* Preview */}
                              {showPreview && (
                                   <div className='rounded-xl border border-gray-200 overflow-hidden bg-[#333]'>
                                        <iframe
                                             ref={previewRef}
                                             title='Email Preview'
                                             className='w-full border-0'
                                             style={{ minHeight: '500px' }}
                                        />
                                   </div>
                              )}

                              {/* Available Variables */}
                              <div className='bg-white rounded-xl p-4 border border-gray-200'>
                                   <h3 className='text-darker text-sm font-bold mb-2'>Available Variables</h3>
                                   <p className='text-darker/30 text-xs mb-3'>
                                        Use these in your template with double curly braces. For conditional blocks use {'{{#variable}}...{{/variable}}'}.
                                   </p>
                                   <div className='flex flex-wrap gap-2'>
                                        {selected.available_variables.map((v) => (
                                             <code
                                                  key={v}
                                                  className='bg-gray-50 px-2 py-1 rounded text-xs text-yellow cursor-pointer hover:bg-yellow/10 duration-200'
                                                  onClick={() => {
                                                       if (!showPreview) {
                                                            setHtmlBody(prev => prev + `{{${v}}}`);
                                                       }
                                                  }}
                                                  title='Click to insert'
                                             >
                                                  {`{{${v}}}`}
                                             </code>
                                        ))}
                                   </div>
                              </div>

                              {/* Save Button */}
                              <div className='flex items-center gap-3'>
                                   <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className='bg-yellow text-black font-bold px-6 py-2.5 rounded-lg hover:bg-yellow/90 duration-300 disabled:opacity-50'
                                   >
                                        {saving ? 'Saving...' : 'Save Template'}
                                   </button>
                                   {saved && <span className='text-green-400 text-sm'>Saved successfully!</span>}
                              </div>
                         </div>
                    )}
               </div>
          </div>
     );
}
